import React from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidEmail, isSunnyHomes, normalizePhoneInput } from "./InputFormatters";
import {
  UserIcon,
  EnvelopeIcon,
  ArrowRightIcon,
  PencilSquareIcon,
  BuildingOfficeIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";

export default function Step1UserDetail({
  phoneNumber,
  setPhoneNumber,
  userForm,
  setUserForm,
  companyType,
  setCompanyType,
  companyForm,
  setCompanyForm,
  isExistingUser,
  loadingLookup,
  onLookupPhone,
  onResetPhone,
  onNext,
  onSubmit,
  isSubmitting,
  lookupError,
}) {
  const handlePhoneChange = (val) => {
    let phoneVal = val || "";
    // If input generated duplicate +6161... or trunk 0 like +610...
    if (phoneVal.startsWith("+6161")) {
      phoneVal = "+61" + phoneVal.slice(5);
    } else if (phoneVal.startsWith("+610")) {
      phoneVal = "+61" + phoneVal.slice(4);
    }
    setPhoneNumber(phoneVal);
    setUserForm((prev) => {
      if (prev.phoneNumber === phoneVal) return prev;
      return {
        ...prev,
        phoneNumber: phoneVal,
      };
    });
  };

  const handlePhonePaste = (e) => {
    const pastedText = e.clipboardData?.getData("text") || "";
    if (!pastedText || !/\d/.test(pastedText)) return;

    e.preventDefault();
    const normalized = normalizePhoneInput(pastedText);
    if (normalized) {
      handlePhoneChange(normalized);
    }
  };

  const handleEmailChange = (e) => {
    setUserForm((prev) => ({ ...prev, email: e.target.value }));
  };

  const handleNameChange = (e) => {
    setUserForm((prev) => ({ ...prev, fullName: e.target.value }));
  };

  const handleCompanyChange = (e) => {
    const rawVal = e.target.value;
    const isInternal = isSunnyHomes(rawVal);
    
    if (isInternal) {
      setCompanyType("sunny_homes");
      setCompanyForm({
        companyName: rawVal,
        companyCode: "SH",
        abn: "",
        phoneNumber: "",
        email: "",
      });
    } else {
      setCompanyType("other");
      setCompanyForm((prev) => ({
        ...prev,
        companyName: rawVal,
      }));
    }
  };

  const handleNextPhoneClick = () => {
    if (phoneNumber && isValidPhoneNumber(phoneNumber)) {
      onLookupPhone(phoneNumber);
    }
  };

  const handleReset = () => {
    setPhoneNumber("");
    setUserForm({
      userId: null,
      fullName: "",
      phoneNumber: "",
      email: "",
      externalCompanyId: null,
      isInternalUser: false,
    });
    setCompanyType("other");
    setCompanyForm({
      companyName: "",
      companyCode: "",
      abn: "",
      phoneNumber: "",
      email: "",
    });
    if (onResetPhone) {
      onResetPhone();
    }
  };

  const isPhoneValid = Boolean(phoneNumber && isValidPhoneNumber(phoneNumber));

  const isInternalWorker =
    companyType === "sunny_homes" ||
    isSunnyHomes(companyForm.companyName) ||
    Boolean(userForm.isInternalUser);

  const isFormValid =
    userForm.fullName?.trim().length >= 2 &&
    isPhoneValid &&
    (userForm.email ? isValidEmail(userForm.email) : true) &&
    (companyForm.companyName?.trim().length >= 2);

  const hasFetchedDetails = isExistingUser !== null;
  const isReadOnly = isExistingUser === true;

  return (
    <div className="space-y-4 animate-in fade-in duration-300 font-sans">
      {/* Mobile Phone Input Container */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="block text-[11px] font-bold text-slate-700">
            Mobile Phone Number <span className="text-rose-500">*</span>
          </label>
          {hasFetchedDetails && (
            <button
              type="button"
              onClick={handleReset}
              className="inline-flex items-center gap-1 text-[11px] font-bold text-primary-600 hover:text-primary-700 cursor-pointer hover:underline transition-all"
            >
              <PencilSquareIcon className="w-3.5 h-3.5" />
              <span>Change Number</span>
            </button>
          )}
        </div>

        <div
          onPaste={handlePhonePaste}
          className={`relative flex items-center rounded-2xl border transition-all shadow-xs p-2.5 ${
            hasFetchedDetails
              ? "border-slate-200 bg-slate-100/70 opacity-90 cursor-not-allowed"
              : "border-slate-200 bg-slate-50/50 focus-within:bg-white focus-within:border-primary-500 focus-within:ring-4 focus-within:ring-primary-500/10"
          }`}
        >
          <style>{`
            .PhoneInput {
              display: flex;
              align-items: center;
              width: 100%;
              gap: 8px;
            }
            .PhoneInputCountry {
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .PhoneInputCountrySelect {
              cursor: pointer;
            }
            .PhoneInputInput {
              width: 100%;
              background: transparent;
              font-size: 13px;
              font-weight: 600;
              color: #1e293b;
              outline: none;
              border: none;
              font-family: monospace;
            }
            .PhoneInputInput:disabled {
              color: #475569;
              cursor: not-allowed;
            }
          `}</style>
          <PhoneInput
            international
            countryCallingCodeEditable={false}
            defaultCountry="AU"
            value={phoneNumber}
            onChange={handlePhoneChange}
            onPaste={handlePhonePaste}
            disabled={hasFetchedDetails || loadingLookup}
            placeholder="Enter mobile phone number"
          />
        </div>

        {!hasFetchedDetails && phoneNumber && !isValidPhoneNumber(phoneNumber) && phoneNumber.length > 5 && (
          <p className="text-[10px] text-rose-500 font-semibold mt-1">
            Please enter a valid phone number with country code.
          </p>
        )}
      </div>

      {/* 1. Initial State: Show "Next" Button below phone input to call lookup API */}
      {!hasFetchedDetails && (
        <div className="pt-2 flex justify-end">
          <button
            type="button"
            onClick={handleNextPhoneClick}
            disabled={!isPhoneValid || loadingLookup}
            className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 active:scale-[0.98] text-white font-bold text-xs shadow-md hover:shadow-lg hover:shadow-primary-600/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
          >
            {loadingLookup ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                <span>Verifying...</span>
              </>
            ) : (
              <>
                <span>Next</span>
                <ArrowRightIcon className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      )}

      {/* 2. Post-Lookup State: Reveal Full Name, Email, Company Name & Action Button */}
      {hasFetchedDetails && (
        <div className="space-y-4 pt-1 animate-in fade-in slide-in-from-top-3 duration-300">
          <div className="space-y-3">
            {/* Full Name */}
            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1">
                Full Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <UserIcon className="h-4 w-4" />
                </div>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  value={userForm.fullName || ""}
                  onChange={handleNameChange}
                  disabled={isReadOnly}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white pl-10 pr-4 py-3 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-xs disabled:bg-slate-100/80 disabled:text-slate-600 disabled:cursor-not-allowed"
                  required
                />
              </div>
            </div>

            {/* Email Address */}
            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1">
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <EnvelopeIcon className="h-4 w-4" />
                </div>
                <input
                  type="email"
                  placeholder="worker@example.com"
                  value={userForm.email || ""}
                  onChange={handleEmailChange}
                  disabled={isReadOnly}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white pl-10 pr-4 py-3 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-xs disabled:bg-slate-100/80 disabled:text-slate-600 disabled:cursor-not-allowed"
                />
              </div>
              {userForm.email && !isValidEmail(userForm.email) && (
                <p className="text-[10px] text-rose-500 font-semibold mt-1">
                  Please enter a valid email address.
                </p>
              )}
            </div>

            {/* Company Name */}
            <div>
              <label className="block text-[11px] font-bold text-slate-700 mb-1">
                Company Name <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                  <BuildingOfficeIcon className="h-4 w-4" />
                </div>
                <input
                  type="text"
                  placeholder="e.g. Sunny Homes or Acme Plumbing"
                  value={companyForm.companyName || ""}
                  onChange={handleCompanyChange}
                  disabled={isReadOnly}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50/50 focus:bg-white pl-10 pr-4 py-3 text-xs font-semibold text-slate-800 placeholder-slate-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-500/10 transition-all shadow-xs disabled:bg-slate-100/80 disabled:text-slate-600 disabled:cursor-not-allowed"
                  required
                />
              </div>
            </div>
          </div>

          {/* Action Button: Direct Check-in for Internal, Continue for External */}
          <div className="pt-2 flex justify-end">
            {isInternalWorker ? (
              <button
                type="button"
                onClick={onSubmit}
                disabled={!isFormValid || isSubmitting}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 active:scale-[0.98] text-white font-bold text-xs shadow-md hover:shadow-lg hover:shadow-primary-600/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
              >
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    <span>Checking In...</span>
                  </>
                ) : (
                  <>
                    <CheckCircleIcon className="w-4 h-4" />
                    <span>Complete & Check In</span>
                  </>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={onNext}
                disabled={!isFormValid || isSubmitting}
                className="px-6 py-3 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 active:scale-[0.98] text-white font-bold text-xs shadow-md hover:shadow-lg hover:shadow-primary-600/20 transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Continue</span>
                <ArrowRightIcon className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {lookupError && (
        <div className="p-2.5 rounded-xl bg-rose-50 border border-rose-200/80 text-rose-700 text-xs font-bold text-center animate-in fade-in">
          {lookupError}
        </div>
      )}
    </div>
  );
}
